import Collapse from '@mui/material/Collapse';
import { useState, useEffect, useCallback } from 'react';

import { usePathname } from 'src/routes/hooks';
import { useActiveLink } from 'src/routes/hooks/use-active-link';

import { NavListProps, NavSubListProps } from '../types';
import NavItem from './nav-item';

// ----------------------------------------------------------------------

export default function NavList({ data, depth, slotProps }: NavListProps) {
  const pathname = usePathname();

  // Usa deep match para manter o item ativo em subrotas (criar/editar)
  const active = useActiveLink(data.path, true);

  const [openMenu, setOpenMenu] = useState(data.children ? active : active);

  // Verifica se algum dos filhos está ativo
  const hasActiveChild = useCallback(() => {
    if (!data.children) return false;

    return data.children.some((child: any) => {
      // Verifica se o path do child corresponde ao pathname atual
      if (child.path && child.path !== '#' && pathname.startsWith(child.path)) return true;
      if (child.children) {
        return child.children.some((subChild: any) => subChild.path && subChild.path !== '#' && pathname.startsWith(subChild.path));
      }
      return false;
    });
  }, [data.children, pathname]);

  useEffect(() => {
    // Mantém o menu aberto se o item atual está ativo OU se algum filho está ativo
    if (active || hasActiveChild()) {
      setOpenMenu(true);
    }
    // Não fecha automaticamente quando navegar - apenas quando explicitamente fechado pelo usuário
  }, [pathname, active, hasActiveChild]);

  const handleToggleMenu = useCallback(() => {
    if (data.children) {
      setOpenMenu((prev) => !prev);
    }
  }, [data.children]);

  return (
    <>
      <NavItem
        open={openMenu}
        onClick={handleToggleMenu}
        //
        title={data.title}
        path={data.path}
        icon={data.icon}
        info={data.info}
        roles={data.roles}
        caption={data.caption}
        disabled={data.disabled}
        //
        depth={depth}
        hasChild={!!data.children}
        externalLink={data.path.includes('http')}
        currentRole={slotProps?.currentRole}
        //
        active={active}
        className={active ? 'active' : ''}
        sx={{
          mb: `${slotProps?.gap}px`,
          ...(depth === 1 ? slotProps?.rootItem : slotProps?.subItem),
        }}
      />

      {!!data.children && (
        <Collapse in={openMenu} unmountOnExit>
          <NavSubList data={data.children} depth={depth} slotProps={slotProps} />
        </Collapse>
      )}
    </>
  );
}

// ----------------------------------------------------------------------

function NavSubList({ data, depth, slotProps }: NavSubListProps) {
  return (
    <>
      {data.map((list) => (
        <NavList key={list.title} data={list} depth={depth + 1} slotProps={slotProps} />
      ))}
    </>
  );
}
